import { useState } from 'react';
import {
  Button,
  ButtonIntent,
  ButtonTier,
  Card,
  CardContent,
  FormGroup,
  Input,
  Label,
  Select,
} from 'shared-components';
import { SelectorPreview } from './selector-preview';
import { sortOptions } from './selector-editor-utils';
import { SelectorTemplateDraft } from './use-selector-template-draft';
import styles from './selector-form-view.module.css';

export const SelectorFormView = ({
  state,
}: {
  state: SelectorTemplateDraft;
}) => {
  const {
    draft,
    nodes,
    productsById,
    productOptions,
    uncoveredProducts,
    validation,
    updateDraft,
    updateNode,
    updateOption,
    handleAddOption,
    handleRemoveNode,
    handleRemoveOption,
    handleMoveOption,
  } = state;

  const [dragging, setDragging] = useState<{
    nodeId: string;
    optionId: string;
  } | null>(null);
  const [optionSearch, setOptionSearch] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);

  if (!draft) return null;

  return (
    <div className={styles.formLayout}>
      <div className={styles.formColumn}>
        <div className={styles.formActions}>
          <Button
            tier={ButtonTier.Secondary}
            onClick={() => setShowPreview((prev) => !prev)}
          >
            {showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
        </div>

        {nodes.map((node) => {
          const options = sortOptions(node.options);

          return (
            <Card key={node.id} className={styles.nodeCard}>
              <CardContent>
                <div className={styles.nodeHeader}>
                  <div>
                    <div className={styles.nodeTitleRow}>
                      <span className={styles.nodeTitle}>Question</span>
                      {node.id === draft.tree.rootNodeId && (
                        <span className={styles.rootBadge}>Start</span>
                      )}
                    </div>
                    <div className={styles.nodeId}>{node.id}</div>
                  </div>
                  <div className={styles.nodeActions}>
                    {node.id !== draft.tree.rootNodeId && (
                      <Button
                        tier={ButtonTier.Tertiary}
                        onClick={() => {
                          updateDraft((current) => ({
                            ...current,
                            tree: {
                              ...current.tree,
                              rootNodeId: node.id,
                            },
                          }));
                        }}
                        disabled={draft.isArchived}
                      >
                        Set as Start
                      </Button>
                    )}
                    <Button
                      tier={ButtonTier.Tertiary}
                      intent={ButtonIntent.Negative}
                      onClick={() => handleRemoveNode(node.id)}
                      disabled={draft.isArchived}
                    >
                      Remove
                    </Button>
                  </div>
                </div>

                <FormGroup>
                  <Label htmlFor={`prompt-${node.id}`}>Prompt</Label>
                  <Input
                    id={`prompt-${node.id}`}
                    value={node.prompt}
                    onChange={(event) => {
                      const value = event.target.value;
                      updateNode(node.id, (currentNode) => ({
                        ...currentNode,
                        prompt: value,
                      }));
                    }}
                    disabled={draft.isArchived}
                  />
                </FormGroup>

                <div className={styles.optionsHeader}>
                  <Label>Options</Label>
                  <Button
                    tier={ButtonTier.Secondary}
                    onClick={() => handleAddOption(node.id)}
                    disabled={draft.isArchived}
                  >
                    Add Option
                  </Button>
                </div>

                <div className={styles.optionsList}>
                  {options.map((option) => {
                    const searchTerm = optionSearch[option.id] ?? '';
                    const filteredProducts = productOptions.filter(
                      (product) =>
                        product.name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    );
                    const missingProduct =
                      option.next.type === 'product' &&
                      option.next.productId &&
                      !productsById.has(option.next.productId);
                    const missingNode =
                      option.next.type === 'node' &&
                      option.next.nodeId &&
                      !draft.tree.nodes[option.next.nodeId];

                    return (
                      <div
                        key={option.id}
                        className={styles.optionRow}
                        draggable={!draft.isArchived}
                        onDragStart={() => {
                          setDragging({
                            nodeId: node.id,
                            optionId: option.id,
                          });
                        }}
                        onDragOver={(event) => {
                          if (dragging?.nodeId === node.id) {
                            event.preventDefault();
                          }
                        }}
                        onDrop={() => {
                          if (dragging?.nodeId !== node.id) return;
                          handleMoveOption(
                            node.id,
                            dragging.optionId,
                            option.id
                          );
                          setDragging(null);
                        }}
                        onDragEnd={() => {
                          setDragging(null);
                        }}
                      >
                        <div className={styles.dragHandle}>⋮⋮</div>
                        <Input
                          value={option.label}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateOption(
                              node.id,
                              option.id,
                              (currentOption) => ({
                                ...currentOption,
                                label: value,
                              })
                            );
                          }}
                          disabled={draft.isArchived}
                        />
                        <Select
                          value={option.next.type}
                          onChange={(event) => {
                            const value = event.target.value as
                              | 'none'
                              | 'node'
                              | 'product';
                            updateOption(
                              node.id,
                              option.id,
                              (currentOption) => {
                                if (value === 'none') {
                                  return {
                                    ...currentOption,
                                    next: { type: 'none' },
                                  };
                                }
                                if (value === 'node') {
                                  const fallbackNodeId =
                                    Object.keys(draft.tree.nodes)[0] ?? '';
                                  return {
                                    ...currentOption,
                                    next: {
                                      type: 'node',
                                      nodeId: fallbackNodeId,
                                    },
                                  };
                                }

                                const fallbackProductId =
                                  productOptions[0]?._id.toString() ?? '';
                                return {
                                  ...currentOption,
                                  next: {
                                    type: 'product',
                                    productId: fallbackProductId,
                                  },
                                };
                              }
                            );
                          }}
                          disabled={draft.isArchived}
                        >
                          <option value="none">Not connected</option>
                          <option value="product">Product</option>
                          <option value="node">Question</option>
                        </Select>

                        <div className={styles.targetPicker}>
                          {option.next.type === 'product' ? (
                            <>
                              <Input
                                value={searchTerm}
                                placeholder="Search products"
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setOptionSearch((current) => ({
                                    ...current,
                                    [option.id]: value,
                                  }));
                                }}
                                disabled={draft.isArchived}
                              />
                              <Select
                                value={option.next.productId}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  updateOption(
                                    node.id,
                                    option.id,
                                    (currentOption) => ({
                                      ...currentOption,
                                      next: {
                                        type: 'product',
                                        productId: value,
                                      },
                                    })
                                  );
                                }}
                                disabled={draft.isArchived}
                              >
                                {missingProduct && (
                                  <option value={option.next.productId}>
                                    Missing product ({option.next.productId})
                                  </option>
                                )}
                                {filteredProducts.map((product) => (
                                  <option
                                    key={product._id.toString()}
                                    value={product._id.toString()}
                                  >
                                    {product.name}
                                  </option>
                                ))}
                              </Select>
                            </>
                          ) : option.next.type === 'node' ? (
                            <Select
                              value={option.next.nodeId}
                              onChange={(event) => {
                                const value = event.target.value;
                                updateOption(
                                  node.id,
                                  option.id,
                                  (currentOption) => ({
                                    ...currentOption,
                                    next: {
                                      type: 'node',
                                      nodeId: value,
                                    },
                                  })
                                );
                              }}
                              disabled={draft.isArchived}
                            >
                              {missingNode && (
                                <option value={option.next.nodeId}>
                                  Missing question ({option.next.nodeId})
                                </option>
                              )}
                              {nodes.map((targetNode) => (
                                <option
                                  key={targetNode.id}
                                  value={targetNode.id}
                                >
                                  {targetNode.prompt || 'Untitled question'} (
                                  {targetNode.id})
                                </option>
                              ))}
                            </Select>
                          ) : null}
                        </div>

                        <Button
                          tier={ButtonTier.Tertiary}
                          intent={ButtonIntent.Negative}
                          onClick={() => {
                            handleRemoveOption(node.id, option.id);
                          }}
                          disabled={draft.isArchived}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}

        <Button
          tier={ButtonTier.Secondary}
          onClick={() => state.handleAddNode()}
          className={styles.addNodeButton}
          disabled={draft.isArchived}
        >
          Add Question
        </Button>
      </div>

      <div className={styles.sideColumn}>
        {showPreview && (
          <Card>
            <CardContent>
              <div className={styles.panelTitle}>Preview</div>
              <SelectorPreview
                tree={draft.tree}
                productsById={productsById}
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <div className={styles.panelTitle}>Unassigned Products</div>
            <div className={styles.panelSubtitle}>
              Public, confirmed products not referenced in this template.
            </div>
            {uncoveredProducts.length === 0 ? (
              <div className={styles.validationEmpty}>
                All eligible products are covered.
              </div>
            ) : (
              <ul className={styles.validationList}>
                {uncoveredProducts.map((product) => (
                  <li key={product._id.toString()}>{product.name}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className={styles.panelTitle}>Validation</div>
            <div className={styles.validationSummary}>
              {validation.valid ? '✅ Valid' : '⚠️ Needs attention'}
            </div>

            <div className={styles.validationSection}>
              <div className={styles.validationSectionTitle}>Errors</div>
              {validation.errors.length === 0 ? (
                <div className={styles.validationEmpty}>No errors</div>
              ) : (
                <ul className={styles.validationList}>
                  {validation.errors.map((error, index) => (
                    <li key={`${error.code}-${index}`}>{error.message}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.validationSection}>
              <div className={styles.validationSectionTitle}>Warnings</div>
              {validation.warnings.length === 0 ? (
                <div className={styles.validationEmpty}>No warnings</div>
              ) : (
                <ul className={styles.validationList}>
                  {validation.warnings.map((warning, index) => (
                    <li key={`${warning.code}-${index}`}>
                      {warning.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
